/**
 * WlTenantAddonsCard
 * ----------------------------------------------------------------------------
 * Bloco "Add-ons contratados" do modal WL → Faturamento.
 *
 * Permite ao operador WL:
 *   - Visualizar add-ons recorrentes ativos do tenant filho (snapshot V1)
 *   - Adicionar novo add-on recorrente disponível
 *   - Alterar quantidade (apenas para add-ons que aceitam max_quantity > 1)
 *   - Remover (cancelar) add-on recorrente
 *
 * Todas as operações passam por RPCs `wl_tenant_addon_*` que internamente
 * disparam `tenant_addon_apply_change` para sincronizar a assinatura master
 * (gateway uaizy) e reprovisionar os limites do tenant.
 *
 * RELEASE: 2026-08-18 v1.1.0 - Modal "Adicionar add-on": filtro por tipo de
 *          integração (Todos/Legado/V1), exibição do preço real dos add-ons V1
 *          (plan_addon_recurrence_options via wl_list_addons v1.6.0) e
 *          restrição aos add-ons compatíveis com a periodicidade do binding
 *          do tenant (wl_get_tenant_plan_binding v1.2.0).
 * RELEASE: 2026-06-15 v1.0.0

 */
import { useMemo, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  useWlTenantAddons,
  useWlTenantAddonMutations,
  type WlTenantAddonItem,
} from "@/hooks/wl/useWlTenantDetails";
import { useWlAddons, type WlAddon } from "@/hooks/useWlAddons";
import { useWlTenantPlanBinding } from "@/hooks/wl/useWlTenantPlanBinding";

/** Formata BRL — local helper para evitar import circular. */
function fmtBRL(v: number | null | undefined): string {
  return (Number(v) || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

/** Rótulo amigável da periodicidade (usado nos preços V1). */
function labelRecorrencia(r?: string | null): string {
  switch (r) {
    case "mensal":
      return "mês";
    case "trimestral":
      return "trimestre";
    case "semestral":
      return "semestre";
    case "anual":
      return "ano";
    default:
      return r || "mês";
  }
}

/**
 * Resolve o preço exibido do add-on:
 *  - V1 (integracao_v1): preço da opção de recorrência do ciclo do tenant.
 *  - Legado: price_monthly da raiz.
 * Retorna null quando o add-on V1 não possui opção para o ciclo informado.
 */
function resolveAddonPrice(
  addon: WlAddon,
  recurrence: string | null,
): { price: number; suffix: string } | null {
  if (addon.billing_integration !== "integracao_v1") {
    return { price: addon.price_monthly, suffix: "mês" };
  }
  const opt = (addon.recurrence_options ?? []).find(
    (o) => o.recurrence_interval === (recurrence || "mensal"),
  );
  if (!opt) return null;
  return { price: opt.price, suffix: labelRecorrencia(opt.recurrence_interval) };
}


export function WlTenantAddonsCard({ tenantId }: { tenantId: string }) {
  // Lista canônica de add-ons contratados (tenant_addons via RPC v1.1.0)
  const addonsQ = useWlTenantAddons(tenantId, "active");
  // Catálogo global de add-ons disponíveis (para o seletor)
  const catalogQ = useWlAddons({ includeInactive: false });
  // Binding de cobrança do tenant — define a periodicidade compatível (V1)
  const bindingQ = useWlTenantPlanBinding(tenantId);
  const tenantRecurrence = bindingQ.data?.recurrence_interval ?? null;
  const hasV1Binding = Boolean(bindingQ.data?.binding_id && tenantRecurrence);

  const { add, setQuantity, remove } = useWlTenantAddonMutations(tenantId);

  // Modal de adicionar
  const [addOpen, setAddOpen] = useState(false);
  const [addSelected, setAddSelected] = useState<string>("");
  const [addQty, setAddQty] = useState<number>(1);
  // v1.1.0 — filtro por tipo de integração no modal de adicionar
  const [addIntegration, setAddIntegration] = useState<"all" | "legado" | "integracao_v1">(
    "all"
  );

  // Modal de editar quantidade
  const [editTarget, setEditTarget] = useState<WlTenantAddonItem | null>(null);
  const [editQty, setEditQty] = useState<number>(1);

  // Confirmação de remoção
  const [removeTarget, setRemoveTarget] = useState<WlTenantAddonItem | null>(null);

  // Apenas add-ons recorrentes (avulsos seguem ledger separado — referral, etc)
  const contracted = addonsQ.data ?? [];
  const contractedIds = useMemo(
    () => new Set(contracted.map((a) => a.addon_id)),
    [contracted]
  );

  // Catálogo disponível para adicionar:
  //  - recorrentes ainda não contratados;
  //  - respeitando o filtro de integração escolhido;
  //  - add-ons V1 apenas quando possuem preço na periodicidade do tenant
  //    (evita contratar add-on trimestral em plano mensal).
  const availableToAdd = useMemo(() => {
    const list = catalogQ.data ?? [];
    return list.filter((a) => {
      if (a.billing_mode !== "recorrente") return false;
      if (contractedIds.has(a.id)) return false;
      if (addIntegration !== "all" && a.billing_integration !== addIntegration) return false;
      if (a.billing_integration === "integracao_v1") {
        // Sem binding V1 não há ciclo definido → não é seguro contratar
        if (!hasV1Binding) return false;
        return resolveAddonPrice(a, tenantRecurrence) !== null;
      }
      return true;
    });
  }, [catalogQ.data, contractedIds, addIntegration, hasV1Binding, tenantRecurrence]);


  // Resolve metadata do catálogo a partir do addon_id (para limites de qty).
  const catalogById = useMemo(() => {
    const map = new Map<string, (typeof catalogQ.data extends Array<infer T> ? T : never)>();
    (catalogQ.data ?? []).forEach((a) => map.set(a.id, a as never));
    return map;
  }, [catalogQ.data]);

  const handleAdd = async () => {
    if (!addSelected) {
      toast.error("Selecione um add-on");
      return;
    }
    try {
      await add.mutateAsync({ addon_id: addSelected, quantity: addQty });
      toast.success("Add-on adicionado com sucesso");
      setAddOpen(false);
      setAddSelected("");
      setAddQty(1);
    } catch (err: any) {
      toast.error(err?.message || "Erro ao adicionar add-on");
    }
  };

  const handleEdit = async () => {
    if (!editTarget) return;
    try {
      await setQuantity.mutateAsync({
        addon_id: editTarget.addon_id,
        quantity: editQty,
      });
      toast.success("Quantidade atualizada");
      setEditTarget(null);
    } catch (err: any) {
      toast.error(err?.message || "Erro ao atualizar quantidade");
    }
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    try {
      await remove.mutateAsync({ addon_id: removeTarget.addon_id });
      toast.success("Add-on removido");
      setRemoveTarget(null);
    } catch (err: any) {
      toast.error(err?.message || "Erro ao remover add-on");
    }
  };

  return (
    <Card className="rounded-xl border border-border/50">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-sm">Add-ons contratados</CardTitle>
          <p className="text-xs text-muted-foreground mt-0.5">
            Fonte canônica: <code>tenant_addons</code> · alterações são aplicadas
            imediatamente sem pro-rata
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            // Abre já no tipo de integração do tenant (V1 quando há binding)
            setAddIntegration(hasV1Binding ? "integracao_v1" : "legado");
            setAddSelected("");
            setAddOpen(true);
          }}
          disabled={addonsQ.isLoading || catalogQ.isLoading}
        >

          <Plus className="h-3.5 w-3.5 mr-1" />
          Adicionar
        </Button>
      </CardHeader>
      <CardContent className="pt-2">
        {addonsQ.isLoading ? (
          <p className="text-xs text-muted-foreground">Carregando add-ons…</p>
        ) : contracted.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            Nenhum add-on recorrente contratado.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Add-on</TableHead>
                <TableHead className="text-xs">Tipo</TableHead>
                <TableHead className="text-xs text-right">Qtd</TableHead>
                <TableHead className="text-xs text-right">Unitário</TableHead>
                <TableHead className="text-xs text-right">Subtotal</TableHead>
                <TableHead className="text-xs">Integração</TableHead>
                <TableHead className="text-xs w-[110px] text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contracted.map((a) => {
                // [fix 2026-07-22] wl_list_tenant_addons v1.3.0 devolve
                // price_monthly como TOTAL mensal normalizado do registro
                // (raw_total / months_divisor, com quantity já embutida).
                // unit_price (V1), quando presente, é per-unit.
                // Para exibir Unitário e Subtotal corretamente sem tocar a RPC
                // (que alimenta o modal financeiro e KPIs), derivamos o
                // unitário dividindo o total pela quantidade quando só temos
                // price_monthly (add-ons legados com unit_price NULL).
                const qty = a.quantity || 1;
                const unit = Number(
                  a.unit_price ?? (Number(a.price_monthly ?? 0) / qty)
                );
                const subtotal = unit * qty;
                const cat = catalogById.get(a.addon_id) as
                  | { max_quantity: number | null }
                  | undefined;
                const allowQty = (cat?.max_quantity ?? 1) > 1;
                return (
                  <TableRow key={a.id}>
                    <TableCell className="text-xs font-medium">
                      {a.addon_name}
                    </TableCell>
                    <TableCell className="text-xs">
                      <Badge variant="outline" className="text-[10px]">
                        {a.addon_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-right">
                      {a.quantity}
                    </TableCell>
                    <TableCell className="text-xs text-right">
                      {fmtBRL(unit)}
                    </TableCell>
                    <TableCell className="text-xs text-right font-medium">
                      {fmtBRL(subtotal)}
                    </TableCell>
                    <TableCell className="text-xs">
                      <Badge
                        variant={
                          a.billing_integration === "integracao_v1"
                            ? "default"
                            : "secondary"
                        }
                        className="text-[10px]"
                      >
                        {a.billing_integration === "integracao_v1"
                          ? `V1 · ${a.gateway_slug ?? "—"}`
                          : "Legado"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {allowQty && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            onClick={() => {
                              setEditTarget(a);
                              setEditQty(a.quantity);
                            }}
                            title="Alterar quantidade"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={() => setRemoveTarget(a)}
                          title="Remover"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* ---------------------------------------------------------------- */}
      {/* Modal: Adicionar add-on                                           */}
      {/* ---------------------------------------------------------------- */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar add-on</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {/* v1.1.0 — filtro por tipo de integração */}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Tipo de integração</label>
              <Select
                value={addIntegration}
                onValueChange={(v) => {
                  setAddIntegration(v as typeof addIntegration);
                  setAddSelected("");
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="legado">Legado</SelectItem>
                  <SelectItem value="integracao_v1">V1 (integração 2.0)</SelectItem>
                </SelectContent>
              </Select>
              {hasV1Binding ? (
                <p className="text-[11px] text-muted-foreground">
                  Add-ons V1 listados apenas na periodicidade do plano do tenant:{" "}
                  <strong>{tenantRecurrence}</strong>.
                </p>
              ) : (
                <p className="text-[11px] text-muted-foreground">
                  Este tenant não possui plano 2.0 vinculado — apenas add-ons legados
                  podem ser contratados.
                </p>
              )}
            </div>

            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Add-on</label>
              <Select value={addSelected} onValueChange={setAddSelected}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um add-on…" />
                </SelectTrigger>
                <SelectContent>
                  {availableToAdd.length === 0 ? (
                    <div className="px-2 py-1.5 text-xs text-muted-foreground">
                      Nenhum add-on recorrente compatível disponível
                    </div>
                  ) : (
                    availableToAdd.map((a) => {
                      // Preço real: V1 usa a opção da periodicidade do tenant
                      const p = resolveAddonPrice(a, tenantRecurrence);
                      return (
                        <SelectItem key={a.id} value={a.id}>
                          {a.name} · {fmtBRL(p?.price ?? 0)}/{p?.suffix ?? "mês"}
                        </SelectItem>
                      );
                    })
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Quantidade</label>
              <Input
                type="number"
                min={1}
                value={addQty}
                onChange={(e) =>
                  setAddQty(Math.max(1, Number(e.target.value) || 1))
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAdd} disabled={add.isPending}>
              {add.isPending && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------------------------------------------------------------- */}
      {/* Modal: Editar quantidade                                          */}
      {/* ---------------------------------------------------------------- */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Alterar quantidade — {editTarget?.addon_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Nova quantidade</label>
            <Input
              type="number"
              min={1}
              value={editQty}
              onChange={(e) =>
                setEditQty(Math.max(1, Number(e.target.value) || 1))
              }
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditTarget(null)}>
              Cancelar
            </Button>
            <Button onClick={handleEdit} disabled={setQuantity.isPending}>
              {setQuantity.isPending && (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              )}
              Atualizar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------------------------------------------------------------- */}
      {/* Modal: Confirmação de remoção                                     */}
      {/* ---------------------------------------------------------------- */}
      <Dialog
        open={!!removeTarget}
        onOpenChange={(o) => !o && setRemoveTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remover add-on?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Você está prestes a remover{" "}
            <strong>{removeTarget?.addon_name}</strong> do tenant. A alteração é
            aplicada imediatamente e o valor recorrente sai do próximo ciclo de
            cobrança.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRemoveTarget(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleRemove}
              disabled={remove.isPending}
            >
              {remove.isPending && (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              )}
              Remover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
